/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { auth, firestoreService } from "services/FirestoreService";

interface PendingItem {
  username: string;
  ownerId: string;
  monsterId: string;
  name: string;
  requested?: string; // 'Shared' | 'Public'
}

interface ReportItem {
  id: string; // violation doc id (for per-report delete of catalog reports)
  source: string; // 'report' | 'rejection'
  reportType: string; // 'violation' | 'copyright' | 'error'
  status: string; // 'open' | 'confirmed' | 'resolved'
  reporterName: string;
  reporterEmail: string;
  offensiveType: string;
  description: string;
  createdAt: number | null;
}

interface ReportedItem {
  username: string;
  ownerId: string;
  monsterId: string;
  monsterKey: string;    // set for built-in catalog monsters (no owner); used for the link back
  monsterSource: string; // catalog monsters: their document source (shown instead of an owner)
  name: string;
  reportCount: number;
  reports: ReportItem[];
}

type Status = 'loading' | 'unauthenticated' | 'unauthorized' | 'error' | 'ready';

// Approver-only moderation page (route: /monsterforge/moderation). Lists all monsters awaiting
// approval and all monsters with violation reports. Each links to the normal monster view, where
// the approver performs the Approve / Reject actions in place.
const ModerationPage: React.FC = () => {
  const [status, setStatus] = useState<Status>('loading');
  const [pending, setPending] = useState<PendingItem[]>([]);
  const [reported, setReported] = useState<ReportedItem[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');

  useEffect(() => {
    // Wait until auth state is known before calling the (auth-gated) function.
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setStatus('unauthenticated');
        return;
      }
      setStatus('loading');
      try {
        const queue = await firestoreService.getModerationQueue();
        setPending(queue.pending);
        setReported(queue.reported);
        setStatus('ready');
      } catch (err: any) {
        // Distinguish a genuine permission problem from any other failure, which previously all
        // showed as "Not authorized".
        console.error('getModerationQueue failed:', err?.code, err?.message);
        if (err?.code === 'functions/unauthenticated') {
          setStatus('unauthenticated');
        } else if (err?.code === 'functions/permission-denied') {
          setStatus('unauthorized');
        } else {
          setErrorMsg(`${err?.code || 'error'}: ${err?.message || 'Unknown error'}`);
          setStatus('error');
        }
      }
    });
    return unsub;
  }, []);

  const monsterLink = (username: string, name: string) =>
    `/monsterforge/mon/${encodeURIComponent(username)}/${encodeURIComponent(name)}`;

  // A reported monster links to its owner's share view, OR — for a built-in catalog monster (no
  // owner) — to the catalog view by its data-model key, which the /mon/ link can't serve.
  const reportedLink = (r: ReportedItem) =>
    r.monsterKey ? `/monsterforge/cat/${encodeURIComponent(r.monsterKey)}` : monsterLink(r.username, r.name);

  // Delete a single catalog report (data-error feedback with no monster doc to act on). Removes it
  // from its group, and drops the group once its last report is gone.
  const deleteCatalogReport = async (r: ReportedItem, rep: ReportItem) => {
    if (!window.confirm('Delete this report? This can\'t be undone.')) {
      return;
    }
    const ok = await firestoreService.deleteReport(rep.id);
    if (!ok) {
      window.alert('Could not delete the report. Please try again.');
      return;
    }
    setReported((prev) => prev
      .map((x) => x === r ? { ...x, reports: x.reports.filter((y) => y.id !== rep.id), reportCount: x.reportCount - 1 } : x)
      .filter((x) => x.reports.length > 0));
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '800px', margin: '20px auto', padding: '20px', background: 'rgba(255,255,255,0.92)',
    color: 'black', borderRadius: '8px', fontFamily: 'arial', textShadow: 'none', textAlign: 'left'
  };

  if (status === 'loading') {
    return <div style={containerStyle}><h2>Loading moderation queue…</h2></div>;
  }
  if (status === 'unauthenticated') {
    return <div style={containerStyle}><h2>Please log in</h2><p>You must be signed in to access moderation. <Link to="/monsterforge">Go to Monster Forge</Link>.</p></div>;
  }
  if (status === 'unauthorized') {
    return <div style={containerStyle}><h2>Not authorized</h2><p>Your account does not have moderation access. <Link to="/monsterforge">Go to Monster Forge</Link>.</p></div>;
  }
  if (status === 'error') {
    return <div style={containerStyle}><h2>Something went wrong</h2><p>Could not load the moderation queue: {errorMsg}</p><p><Link to="/monsterforge">Go to Monster Forge</Link></p></div>;
  }

  return (
    <div style={containerStyle}>
      <div style={{fontSize:"x-small", textAlign:"right"}}>
        <Link to="/monsterforge">← Back</Link>
      </div>
      <div style={{textAlign:"left"}}>
        <div style={{ justifyContent: 'space-between', alignItems: 'baseline', textAlign:"center" }}>
          <h1 style={{ margin: 0 }}>Moderation</h1>
        </div>

        <h2>Pending Approval ({pending.length})</h2>
        {pending.length === 0 && <p>No monsters are awaiting approval.</p>}
        {pending.length > 0 && (
          <ul>
            {pending.map((p) => (
              <li key={`${p.ownerId}/${p.monsterId}`}>
                <Link to={monsterLink(p.username, p.name)}><b>{p.name}</b></Link> &mdash; by {p.username || '(unknown)'}
                {' '}&middot; requests <b>{p.requested === 'Public' ? 'Public' : 'Shared'}</b>
              </li>
            ))}
          </ul>
        )}

        <h2>Reported ({reported.length})</h2>
        {reported.length === 0 && <p>No monsters have been reported.</p>}
        {reported.map((r) => (
          <div key={`${r.ownerId}/${r.monsterId}`} style={{ border: '1px solid #bbb', borderRadius: '6px', padding: '10px', margin: '10px 0' }}>
            <div>
              <Link to={reportedLink(r)}><b>{r.name}</b></Link>
              {' '}&mdash; {r.monsterKey ? `source: ${r.monsterSource || 'catalog'}` : `by ${r.username || '(unknown)'}`}
              {' '}&middot; {r.reportCount} open report{r.reportCount === 1 ? '' : 's'}
            </div>
            <ul style={{ marginTop: '6px' }}>
              {r.reports.map((rep) => (
                <li key={rep.id} style={{ marginBottom: '4px' }}>
                  {rep.reportType === 'error' ? (
                    <b style={{ color: '#1769aa' }}>Error (incorrect data)</b>
                  ) : (
                    <b style={{ color: 'darkred' }}>
                      {rep.reportType === 'copyright' ? 'Copyright' : 'Violation'} &mdash; {rep.offensiveType === 'image' ? 'Image' : rep.offensiveType === 'both' ? 'Text & Image' : 'Text'}
                    </b>
                  )}: {rep.description}
                  {/* Catalog reports have no monster to act on, so a moderator just deletes them here. */}
                  {r.monsterKey && (
                    <>{' '}<button type="button" onClick={() => deleteCatalogReport(r, rep)}
                            title="Delete this report." style={{ fontSize: 'small' }}>Delete</button></>
                  )}
                  <br />
                  <span style={{ fontSize: 'small', color: '#555' }}>
                    &mdash; {rep.reporterName} &lt;{rep.reporterEmail}&gt;
                    {rep.createdAt ? ` on ${new Date(rep.createdAt).toLocaleString()}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          </div>
          
        ))}
      </div>
    </div>
  );
};

export default ModerationPage;
